/**
 * Format a string-serialized USD amount (e.g. "123.456789") for display.
 * Keeps 2 decimal places for values >= $1, up to 6 for sub-dollar amounts.
 */
export function formatUsd(value: string | number): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num) || num === 0) return "$0.00";
  if (Math.abs(num) >= 1)
    return `$${num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  if (Math.abs(num) >= 0.01) return `$${num.toFixed(4)}`;
  return `$${num.toFixed(6)}`;
}

/** Format a token count with comma separators. */
export function formatTokens(value: number): string {
  return value.toLocaleString("en-US");
}

/** Format latency in ms. Returns "—" for null. */
export function formatLatency(ms: number | null): string {
  if (ms === null) return "\u2014";
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
  return `${Math.round(ms)}ms`;
}

/** Format a 0–1 ratio as a percentage string. */
export function formatPercent(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`;
}

/** Format an ISO 8601 timestamp for display. Returns "\u2014" for null/undefined. */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "\u2014";
  return iso.replace("T", " ").replace(/\.\d+([+-]\d{2}:\d{2}|Z)?$/, "").replace(/[+-]\d{2}:\d{2}$/, "").replace(/Z$/, "");
}

/** Format an integer count compactly. */
export function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString("en-US");
}
